import React, { useState } from "react";
import axios from "axios";
import { Download, FileText, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Bouton de téléchargement du programme officiel PDF.
 * Envoie les données de la formation au backend qui génère un PDF
 * institutionnel (entête MGB, footer SIRET/NDA, mise en page A4)
 * destiné aux financeurs (OPCO, France Travail, services RH).
 */
export const DownloadProgrammePdf = ({ formation }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/formations/programme-pdf`,
        {
          code: formation.code,
          title: formation.title,
          description: formation.description,
          domain: formation.domain,
          duration: formation.duration,
          modality: formation.modality,
          groupSize: formation.groupSize,
          price: formation.price,
          publicCible: formation.publicCible,
          prerequis: formation.prerequis,
          objectifs: formation.objectifs,
          programme: formation.programme,
        },
        { responseType: "blob" }
      );

      // Force download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `MGB-School_${formation.code}_Programme.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Programme téléchargé. Bonne lecture !");
    } catch {
      toast.error("Impossible de générer le PDF. Réessayez ou contactez-nous.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-4 mt-3"
      style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}
      data-testid="download-pdf-block"
    >
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-5 w-5 text-[#F07B1D]" />
        <p className="font-bold text-[#003087] text-sm">Document officiel</p>
      </div>
      <p className="text-xs text-gray-600 leading-relaxed mb-3">
        Programme à transmettre à votre OPCO, France Travail ou service RH pour valider votre financement.
      </p>
      <Button
        onClick={handleDownload}
        disabled={downloading}
        variant="outline"
        className="w-full border-[#003087] text-[#003087] font-bold hover:bg-[#003087] hover:text-white transition-all"
        data-testid="download-pdf-btn"
      >
        {downloading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Génération en cours...
          </>
        ) : (
          <>
            <Download className="mr-2 h-4 w-4" />
            Télécharger le programme (PDF)
          </>
        )}
      </Button>
      <p className="text-[10px] text-gray-500 text-center mt-2">
        Mentions légales · Accessibilité · Modalités d'évaluation incluses
      </p>
    </div>
  );
};

export default DownloadProgrammePdf;
